import { useEffect } from 'react';

/**
 * 隱藏 SEO 內容的自定義 Hook
 * 
 * 當 React 組件載入完成後，自動隱藏指定的 SEO 內容元素
 * 這主要用於混合渲染架構中，讓搜尋引擎看到靜態 HTML，
 * 但用戶看到 React 渲染的內容
 * 
 * @param {Object} options - 配置選項
 * @param {string} options.selector - CSS 選擇器，默認為 '.seo-content'
 * @param {number} options.delay - 延遲隱藏時間（毫秒），默認為 0
 * @param {string} options.className - 用於隱藏的 CSS 類名，默認為 'hidden'
 * @param {Function} options.onHide - 隱藏完成後的回調函數
 * 
 * @example
 * // 基本用法
 * useSeoContentHide();
 * 
 * // 自定義選擇器
 * useSeoContentHide({ selector: '.teacher-seo-content' });
 * 
 * // 延遲隱藏
 * useSeoContentHide({ delay: 100 });
 * 
 * // 完整選項
 * useSeoContentHide({
 *   selector: '.custom-seo-content',
 *   delay: 50,
 *   className: 'fade-out',
 *   onHide: (element) => console.log('SEO content hidden')
 * });
 */
const useSeoContentHide = (options = {}) => {
  const {
    selector = '.seo-content',
    delay = 0,
    className = 'hidden',
    onHide
  } = options;

  useEffect(() => {
    const hideContent = () => {
      try {
        const element = document.querySelector(selector);
        if (element) {
          element.classList.add(className);
          
          // 調用隱藏完成回調
          if (typeof onHide === 'function') {
            onHide(element);
          }
        }
      } catch (error) {
        console.warn('useSeoContentHide: Error hiding SEO content:', error);
      }
    };

    if (delay > 0) {
      const timer = setTimeout(hideContent, delay);
      return () => clearTimeout(timer);
    } else {
      hideContent();
    }
  }, [selector, delay, className, onHide]);
};

export default useSeoContentHide;